import { useEffect, useState } from 'react'

import { usePrefersReducedMotion } from '@/ui/usePrefersReducedMotion'

/**
 * Drives a blinking-caret/cursor's on/off phase for as long as the calling
 * component is mounted — the composer's block caret (spec 007 T8.2) and a
 * streaming message's trailing cursor (T8.3) both mount a small dedicated
 * child only while they should blink (focused / stream open), so "always
 * blinking while mounted" is equivalent to "blinking while active", and a
 * remount (unmount + mount) naturally resets the phase to visible — no
 * `setState` call is ever needed directly in an effect body to force that
 * reset (which would trip `react-hooks/set-state-in-effect`).
 *
 * Under `prefers-reduced-motion: reduce` this always returns `true`: the
 * element stays visible and static — presence, not motion, carries the
 * "still here" / "still generating" signal.
 */
export function useBlink(intervalMs = 530): boolean {
  const reducedMotion = usePrefersReducedMotion()
  const [on, setOn] = useState(true)

  useEffect(() => {
    if (reducedMotion) return
    const id = window.setInterval(() => setOn((previous) => !previous), intervalMs)
    return () => window.clearInterval(id)
  }, [reducedMotion, intervalMs])

  return reducedMotion ? true : on
}
